import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, capitalize } from 'lodash';
import moment from 'moment';
import { SectionHeading } from 'web-ui-components/lib/typography/headings';
import SubmittedTransactionTable from '../../../sharedComponents/SubmittedTransactionTable/SubmittedTransactionTable';
import { PaymentReview } from '../../../sharedComponents/PaymentReview/PaymentReview';
import {
	getReviewedPaymentsList,
	isPaymentsRequireAuthorisation,
	getPaymentsTotal,
} from '../../../states/payments/selectors';
import { getCurrentTemplateId, getTemplateName } from '../../../states/templates/selectors';
import {
	getBatchName,
	getBatchAuthorisationStatus,
	getBatchId,
	getBatch,
	getBatchRequiresAuthorisation,
	getBatchAuthorisers,
	getBatchGroupAuthorisers,
	getBatchIsDefaultAuthorisers,
} from '../../../states/batch/selectors';
import paymentPropTypes from '../../../states/payments/propTypes';
import withContent from '../../../containers/withContent';
import reviewContent, { contentPropTypes } from './review.content';
import { dateFormat } from '../../../config/';
import ApiConstants from '../../../config/apiConstants';
import ReviewAuthorisationHeader from './ReviewAuthorisationHeader';
import BatchAuthoriserViewer from './BatchAuthoriserViewer';
import { BatchAuthoriserSelector } from './BatchAuthoriserSelector';
import { getAuthorisationMessage } from '../../../helpers/fieldHelpers';
import { hasGroupApprovalsEnabled } from '../../../states/modes/selectors';
import { getUserId } from '../../../states/user/selectors';

const mapState = (state) => ({
	payments: getReviewedPaymentsList(state),
	isPaymentsRequireAuthorisation: isPaymentsRequireAuthorisation(state),
	batchStatus: getBatchAuthorisationStatus(state),
	batchName: getBatchName(state),
	paymentsTotal: getPaymentsTotal(state),
	templateName: getTemplateName(state),
	currentTemplateId: getCurrentTemplateId(state),
	batchId: getBatchId(state),
	batch: getBatch(state),
	requiresAuth: getBatchRequiresAuthorisation(state),
	authorisers: getBatchAuthorisers(state),
	groupAuthorisers: getBatchGroupAuthorisers(state),
	isDefaultAuthorisers: getBatchIsDefaultAuthorisers(state),
	hasGroupApprovalsEnabled: hasGroupApprovalsEnabled(state),
	userId: getUserId(state),
});

class ReviewBatch extends React.Component {
	constructor(props) {
		super(props);

		this.state = { showAuthModal: false };
	}

	render() {
		const {
			payments,
			batchName,
			templateName,
			currentTemplateId,
			paymentsTotal,
			content,
			batchStatus,
			isPaymentsRequireAuthorisation,
			batchId,
			requiresAuth,
			authorisers,
			groupAuthorisers,
			isDefaultAuthorisers,
			hasGroupApprovalsEnabled,
			userId,
		} = this.props;

		const getDate = ({ date }) => {
			if (moment.isMoment(date)) return date.format(dateFormat);
			return '';
		};

		const {
			fromLabel,
			toLabel,
			amtLabel,
			refLabel,
			dateLabel,
			typeLabel,
			AuthorisationBodyMsg,
			AuthorisationHeader,
			AuthorisationByUserSubTitle,
			selectAuthoriserButton,
			changeAuthoriserButton,
			viewAuthoriserButton,
		} = content;

		const paymentLabels = {
			fromLabel,
			toLabel,
			amtLabel,
			refLabel,
			dateLabel,
			typeLabel,
			viewAuthoriserButton,
			changeAuthoriserButton,
			selectAuthoriserButton,
		};

		const getStatus = (status) => {
			if (status === ApiConstants.obligate) {
				return 'warning';
			}

			return status;
		};

		const status = getStatus(batchStatus);
		const templateTextWrapper = currentTemplateId ? `[${templateName}]` : '';
		const hasNoAvailableAuthorisers = groupAuthorisers && groupAuthorisers.length === 0;
		return (
			<div>
				{isPaymentsRequireAuthorisation && (
					<ReviewAuthorisationHeader
						authorisationTitle={AuthorisationHeader}
						authorisationContent={AuthorisationBodyMsg}
					/>
				)}
				<SectionHeading>{`${batchName} ${templateTextWrapper}`}</SectionHeading>
				<SubmittedTransactionTable
					hasNoAvailableAuthorisers={hasNoAvailableAuthorisers}
					paymentsTotal={paymentsTotal}
					showSidebar={isPaymentsRequireAuthorisation}
					sidebarIconType="AuthorisationLarge"
					sidebarTitle={AuthorisationHeader}
					sidebarContent={getAuthorisationMessage(authorisers, userId, AuthorisationByUserSubTitle)}
					labels={paymentLabels}
					batchId={batchId}
					onSelectAuthorisers={() => this.setState({ showAuthModal: true })}
					requiresAuth={requiresAuth}
					authorisers={authorisers}
					isDefaultAuthorisers={isDefaultAuthorisers}
					onlyShowAuthorisers
					hasGroupApprovalsEnabled={hasGroupApprovalsEnabled}
				>
					{payments.map((payment) => (
						<PaymentReview
							key={payment.id}
							fromAccountName={get(payment, 'fromAccountDetails.displayName', '')}
							fromAccountSortCode={get(payment, 'fromAccountDetails.sortCode', '')}
							fromAccountNumber={get(payment, 'fromAccountDetails.accountNumber', '')}
							fromAccountHolder={get(payment, 'fromAccountDetails.metadata.account_name', '')}
							toAccountName={get(payment, 'toAccountDetails.displayName', '')}
							toAccountSortCode={get(payment, 'toAccountDetails.sortCode', '')}
							toAccountNumber={get(payment, 'toAccountDetails.accountNumber', '')}
							amount={payment.amount}
							reference={payment.reference}
							paymentDate={getDate(payment)}
							paymentType={payment.type === 'single' ? 'One-off' : capitalize(payment.type)}
							status={status}
							labels={paymentLabels}
							hideStatus
							hasStatus={false}
							hasGroupApprovalsEnabled={hasGroupApprovalsEnabled}
						/>
					))}
				</SubmittedTransactionTable>

				{hasGroupApprovalsEnabled ? (
					<BatchAuthoriserViewer
						isOpen={this.state.showAuthModal}
						paymentId={this.state.paymentSelectedId}
						onClose={() => this.setState({ showAuthModal: false })}
					/>
				) : (
					<BatchAuthoriserSelector
						isOpen={this.state.showAuthModal}
						paymentId={this.state.paymentSelectedId}
						onClose={() => this.setState({ showAuthModal: false })}
					/>
				)}
			</div>
		);
	}
}

ReviewBatch.propTypes = {
	payments: PropTypes.arrayOf(PropTypes.shape(paymentPropTypes)).isRequired,
	isPaymentsRequireAuthorisation: PropTypes.bool.isRequired,
	batchName: PropTypes.string,
	paymentsTotal: PropTypes.string,
	content: PropTypes.shape(contentPropTypes),
	batchStatus: PropTypes.string,
	templateName: PropTypes.string,
	currentTemplateId: PropTypes.string,
	batchId: PropTypes.string,
	batch: PropTypes.shape({}),
	requiresAuth: PropTypes.bool,
	authorisers: PropTypes.arrayOf(PropTypes.shape({})),
	isDefaultAuthorisers: PropTypes.bool,
};

const contentWrapper = withContent({ content: reviewContent });
export default connect(mapState)(contentWrapper(ReviewBatch));
export const ConnectWrapper = connect(mapState)(ReviewBatch);
